import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Crown, FileCsv, Users, ChatCircleText, Lock, Heart, Smiley, SmileyMeh, SmileySad, Tray, Medal, Sparkle, CheckCircle, WarningCircle, MagnifyingGlass, TestTube, Play, ArrowClockwise, Trash } from "@phosphor-icons/react";

const SENTIMENT_META = {
  love: { label: "Love it", Icon: Heart, cls: "text-[#B47A3C]", bar: "#B47A3C" },
  good: { label: "Good", Icon: Smiley, cls: "text-[#047857]", bar: "#7FB77E" },
  confused: { label: "Confused", Icon: SmileyMeh, cls: "text-[#92400E]", bar: "#E3B341" },
  frustrated: { label: "Frustrated", Icon: SmileySad, cls: "text-[#B91C1C]", bar: "#D96C57" },
};

const RATING_META = {
  nailed: { label: "Nailed it", Icon: CheckCircle, cls: "text-[#047857]", bar: "#7FB77E" },
  solid: { label: "Solid", Icon: Sparkle, cls: "text-[#B47A3C]", bar: "#B47A3C" },
  insufficient: { label: "Insufficient", Icon: WarningCircle, cls: "text-[#B91C1C]", bar: "#D96C57" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch (e) { return "—"; }
};

export default function FoundingAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [tab, setTab] = useState("signups");
  const [feedback, setFeedback] = useState(null);
  const [paidSeats, setPaidSeats] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [backtest, setBacktest] = useState(null);
  const [backtestJob, setBacktestJob] = useState(null);
  const [backtestBusy, setBacktestBusy] = useState(false);

  useEffect(() => {
    api.get("/founding/list")
      .then(({ data }) => setData(data))
      .catch((e) => { if (e?.response?.status === 403) setDenied(true); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === "feedback" && !feedback) {
      api.get("/feedback").then(({ data }) => setFeedback(data)).catch(() => {});
    }
  }, [tab, feedback]);

  useEffect(() => {
    if (tab === "paid" && !paidSeats) {
      api.get("/founding/paid-seats").then(({ data }) => setPaidSeats(data)).catch(() => {});
    }
  }, [tab, paidSeats]);

  useEffect(() => {
    if (tab === "ratings" && !ratings) {
      api.get("/admin/finder-ratings?days=30").then(({ data }) => setRatings(data)).catch(() => {});
    }
  }, [tab, ratings]);

  const loadBacktest = async () => {
    try {
      const [statusRes, resRes] = await Promise.all([
        api.get("/admin/backtest/status"),
        api.get("/admin/backtest/results"),
      ]);
      setBacktestJob(statusRes.data?.job || null);
      setBacktest(resRes.data?.has_results ? resRes.data : { has_results: false });
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (tab === "backtest" && !backtest) loadBacktest();
  }, [tab, backtest]);

  // Poll while a job is running
  useEffect(() => {
    if (tab !== "backtest") return;
    if (!backtestJob || backtestJob.status !== "running") return;
    const t = setInterval(loadBacktest, 3000);
    return () => clearInterval(t);
  }, [tab, backtestJob]);

  const runBacktest = async (limit) => {
    setBacktestBusy(true);
    try {
      await api.post("/admin/backtest/run", { limit });
      await loadBacktest();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to start backtest");
    }
    setBacktestBusy(false);
  };

  const clearBacktest = async () => {
    if (!window.confirm("Clear all backtest data? This can't be undone.")) return;
    setBacktestBusy(true);
    try {
      await api.delete("/admin/backtest/all");
      setBacktest(null);
      setBacktestJob(null);
      await loadBacktest();
    } catch (e) { /* ignore */ }
    setBacktestBusy(false);
  };

  const releaseSeat = async (seat) => {
    const revertNote = seat.plan === "founding" ? " and revert their plan to free" : "";
    if (!window.confirm(`Release Seat #${seat.founding_spot} held by ${seat.email}? This will clear their paid founding seat${revertNote}. Remaining seats are renumbered.`)) return;
    try {
      await api.post("/founding/paid-seats/release", { user_id: seat.user_id });
      toast.success(`Seat #${seat.founding_spot} released — the public counter is updated.`);
      const { data } = await api.get("/founding/paid-seats");
      setPaidSeats(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't release the seat — please try again.");
    }
  };

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/founding/list.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "founding_sellers.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Founding sellers CSV downloaded.");
    } catch (e) {
      toast.error("Couldn't export — please try again.");
    } finally {
      setDownloading(false);
    }
  };

  if (denied) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6" data-testid="founding-admin-denied">
        <div className="text-center max-w-md">
          <Lock size={40} weight="fill" className="text-[#8A867D] mx-auto mb-4" />
          <h1 className="font-serif-display text-3xl font-bold mb-2">Team access only</h1>
          <p className="text-[15px] text-[#525252] mb-6">This area shows Founding Council signups and is limited to the Vintage Vantage team.</p>
          <button onClick={() => navigate("/dashboard")} data-testid="admin-back-dashboard" className="h-12 px-6 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium">Back to dashboard</button>
        </div>
      </div>
    );
  }

  const signups = data?.signups || [];
  const withNotes = signups.filter((s) => (s.note || "").trim()).length;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="founding-admin-page">
      <header className="sticky top-0 z-30 border-b border-[#D8D3C8] bg-[#FDFBF7]/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/portfolio")} data-testid="admin-back-button" className="inline-flex items-center gap-2 font-medium hover:text-[#B47A3C] transition-colors">
            <ArrowLeft size={18} weight="bold" /> Portfolio
          </button>
          <span className="text-sm font-semibold text-[#525252] truncate max-w-[200px]">{user?.name || user?.email}</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 mb-2"><Crown size={22} weight="fill" className="text-[#B47A3C]" /> <span className="label-caps">Founding Council · feedback funnel</span></div>
        <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-none font-medium mb-3">Who's shaping this</h1>
        <p className="text-lg text-[#525252] leading-relaxed max-w-2xl mb-8">
          Every dealer who's claimed a founding spot — and the headaches they told you about. Read them, reach out, build for them.
        </p>

        <div className="flex items-center gap-2 mb-8" data-testid="admin-tabs">
          {[["signups", "Founding signups", Crown], ["paid", "Paid seats", Medal], ["feedback", "Feedback inbox", Tray], ["ratings", "Finder ratings", Sparkle], ["backtest", "Backtests", TestTube]].map(([key, label, Icon]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`} className={`h-11 px-5 inline-flex items-center gap-2 border font-semibold text-sm transition-colors ${tab === key ? "bg-[#0D1B2A] text-[#FDFBF7] border-[#0D1B2A]" : "bg-white text-[#0D1B2A] border-[#D8D3C8] hover:border-[#0D1B2A]"}`}>
              <Icon size={16} weight="fill" /> {label}
            </button>
          ))}
        </div>

        {tab === "paid" ? (
          <div data-testid="admin-paid-seats">
            <div className="border border-[#B47A3C]/40 bg-[#B47A3C]/10 p-4 mb-6 text-sm text-[#3A3730] leading-relaxed" data-testid="paid-seats-explainer">
              These are <strong>paid</strong> Founding Council seats — assigned only after a completed Stripe checkout.
              This is exactly what the public landing-page counter shows. If a seat looks bogus (e.g. a test-mode
              checkout from before Stripe went live), release it to keep the count honest.
            </div>
            <div className="border border-[#0D1B2A]/20 bg-white overflow-hidden" data-testid="paid-seats-table">
              {!paidSeats ? (
                <div className="p-10 text-center text-[#8A867D]">Loading paid seats…</div>
              ) : !paidSeats.seats?.length ? (
                <div className="p-10 text-center text-[#525252]" data-testid="paid-seats-empty">
                  No paid seats yet — the public counter shows all {paidSeats.spots_total} seats open.
                  The moment a real $29 Founding checkout completes, the seat holder appears here.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[15px]">
                    <thead>
                      <tr className="text-left border-b border-[#F0EFEA] bg-[#F5F3ED]">
                        <th className="px-4 py-3 label-caps font-semibold">Seat</th>
                        <th className="px-4 py-3 label-caps font-semibold">Name</th>
                        <th className="px-4 py-3 label-caps font-semibold">Email</th>
                        <th className="px-4 py-3 label-caps font-semibold">Plan</th>
                        <th className="px-4 py-3 label-caps font-semibold">Last payment</th>
                        <th className="px-4 py-3 label-caps font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paidSeats.seats.map((s, i) => (
                        <tr key={s.user_id} data-testid={`paid-seat-row-${i}`} className={`border-b border-[#F7F6F1] align-top ${i % 2 ? "bg-[#FDFBF7]" : "bg-white"}`}>
                          <td className="px-4 py-3 font-semibold text-[#B47A3C]">#{s.founding_spot}</td>
                          <td className="px-4 py-3 font-medium text-[#0D1B2A]">{s.name || "—"}</td>
                          <td className="px-4 py-3 text-[#525252]"><a href={`mailto:${s.email}`} className="hover:text-[#B47A3C] hover:underline">{s.email}</a></td>
                          <td className="px-4 py-3 text-[#525252] whitespace-nowrap">{s.plan || "—"}</td>
                          <td className="px-4 py-3 text-[#8A867D] whitespace-nowrap">
                            {s.last_payment_at ? `${fmtDate(s.last_payment_at)}${s.last_payment_amount ? ` · $${s.last_payment_amount}` : ""}` : <span className="text-[#B91C1C] font-semibold">No live payment found</span>}
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => releaseSeat(s)} data-testid={`release-seat-btn-${i}`} className="h-9 px-3 border border-[#B91C1C] text-[#B91C1C] text-sm font-semibold hover:bg-[#B91C1C] hover:text-white transition-colors">
                              Release seat
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : tab === "feedback" ? (
          <div data-testid="admin-feedback-inbox">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="feedback-metrics">
              {Object.entries(SENTIMENT_META).map(([key, m]) => (
                <div key={key} data-testid={`feedback-metric-${key}`} className="border border-[#0D1B2A]/20 bg-white p-5">
                  <m.Icon size={22} weight="fill" className={`${m.cls} mb-2`} />
                  <p className="font-serif-display text-3xl font-bold leading-none">{feedback ? (feedback.counts?.[key] ?? 0) : "…"}</p>
                  <p className="label-caps mt-2">{m.label}</p>
                </div>
              ))}
            </div>
            {feedback?.by_page?.length > 0 && (
              <div className="border border-[#0D1B2A]/20 bg-white p-6 mb-8" data-testid="mood-by-page">
                <p className="label-caps mb-1">Mood by page</p>
                <p className="text-sm text-[#8A867D] mb-5">Where sellers feel it — the flywheel telling you exactly where to aim next.</p>
                <div className="space-y-4">
                  {feedback.by_page.map((p) => (
                    <div key={p.page} data-testid={`mood-page-${(p.page || "root").replace(/[^a-z0-9]+/gi, "-")}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-semibold text-[#0D1B2A]">{p.page}</span>
                        <span className="text-xs text-[#8A867D]">{p.total} note{p.total === 1 ? "" : "s"}</span>
                      </div>
                      <div className="flex h-3.5 w-full overflow-hidden rounded-full bg-[#F0EFEA]">
                        {Object.keys(SENTIMENT_META).map((k) => p.counts[k] > 0 ? (
                          <div key={k} title={`${SENTIMENT_META[k].label}: ${p.counts[k]}`} style={{ width: `${(p.counts[k] / p.total) * 100}%`, background: SENTIMENT_META[k].bar }} />
                        ) : null)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-2 mt-5">
                  {Object.entries(SENTIMENT_META).map(([k, m]) => (
                    <span key={k} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#525252]">
                      <span className="w-3 h-3 rounded-full inline-block" style={{ background: m.bar }} /> {m.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="border border-[#0D1B2A]/20 bg-white overflow-hidden" data-testid="feedback-table">
              {!feedback ? (
                <div className="p-10 text-center text-[#8A867D]">Loading feedback…</div>
              ) : !feedback.items?.length ? (
                <div className="p-10 text-center text-[#525252]" data-testid="feedback-empty">No feedback yet — it'll land here the moment a member taps "How are we doing?"</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[15px]">
                    <thead>
                      <tr className="text-left border-b border-[#F0EFEA] bg-[#F5F3ED]">
                        <th className="px-4 py-3 label-caps font-semibold">Mood</th>
                        <th className="px-4 py-3 label-caps font-semibold">What they said</th>
                        <th className="px-4 py-3 label-caps font-semibold">Where</th>
                        <th className="px-4 py-3 label-caps font-semibold">Who</th>
                        <th className="px-4 py-3 label-caps font-semibold">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feedback.items.map((f, i) => {
                        const m = SENTIMENT_META[f.sentiment] || SENTIMENT_META.good;
                        return (
                          <tr key={f.id} data-testid={`feedback-row-${i}`} className={`border-b border-[#F7F6F1] align-top ${i % 2 ? "bg-[#FDFBF7]" : "bg-white"}`}>
                            <td className="px-4 py-3 whitespace-nowrap"><span className={`inline-flex items-center gap-1.5 font-semibold ${m.cls}`}><m.Icon size={17} weight="fill" /> {m.label}</span></td>
                            <td className="px-4 py-3 text-[#3A3730] max-w-md">{(f.comment || "").trim() || <span className="text-[#B8B4AC]">—</span>}</td>
                            <td className="px-4 py-3 text-[#8A867D] whitespace-nowrap">{f.page || "—"}</td>
                            <td className="px-4 py-3 text-[#525252] whitespace-nowrap">{f.user_name || f.user_email || "—"}</td>
                            <td className="px-4 py-3 text-[#8A867D] whitespace-nowrap">{fmtDate(f.created_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : tab === "ratings" ? (
          <div data-testid="admin-ratings-tab">
            <p className="text-sm text-[#525252] leading-relaxed mb-6 max-w-2xl">
              Every Member rating on a Finder scan lands here. The percentages tell you how sharp the Finder feels
              to dealers; the <strong>Insufficient notes</strong> tell you exactly where it's stumbling — in your
              dealers' own words. That's the flywheel target list.
            </p>

            {!ratings ? (
              <div className="p-10 text-center text-[#8A867D] border border-[#0D1B2A]/20 bg-white">Loading ratings…</div>
            ) : ratings.total_ratings === 0 ? (
              <div className="p-10 text-center text-[#525252] border border-[#0D1B2A]/20 bg-white" data-testid="admin-ratings-empty">
                No ratings in the last {ratings.window_days} days yet. Once Members start rating scans on the
                Fantastic Finds page, the trends and note list will populate here.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-ratings-metrics">
                  <div data-testid="ratings-metric-total" className="border border-[#0D1B2A]/20 bg-white p-5">
                    <MagnifyingGlass size={22} weight="fill" className="text-[#0D1B2A] mb-2" />
                    <p className="font-serif-display text-3xl font-bold leading-none">{ratings.total_ratings}</p>
                    <p className="label-caps mt-2">Ratings · last {ratings.window_days}d</p>
                    <p className="text-xs text-[#8A867D] mt-1">{ratings.total_all_time} all-time</p>
                  </div>
                  {Object.entries(RATING_META).map(([key, m]) => (
                    <div key={key} data-testid={`ratings-metric-${key}`} className="border border-[#0D1B2A]/20 bg-white p-5">
                      <m.Icon size={22} weight="fill" className={`${m.cls} mb-2`} />
                      <p className="font-serif-display text-3xl font-bold leading-none">{ratings.percentages?.[key] ?? 0}<span className="text-lg">%</span></p>
                      <p className="label-caps mt-2">{m.label}</p>
                      <p className="text-xs text-[#8A867D] mt-1">{ratings.counts?.[key] ?? 0} rating{(ratings.counts?.[key] ?? 0) === 1 ? "" : "s"}</p>
                    </div>
                  ))}
                </div>

                {ratings.by_category?.length > 0 && (
                  <div className="border border-[#0D1B2A]/20 bg-white p-6 mb-8" data-testid="ratings-by-category">
                    <p className="label-caps mb-1">Accuracy by category</p>
                    <p className="text-sm text-[#8A867D] mb-5">Where the Finder is sharpest — and where it needs more metadata.</p>
                    <div className="space-y-4">
                      {ratings.by_category.map((c) => (
                        <div key={c.category} data-testid={`ratings-cat-${c.category.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-semibold text-[#0D1B2A]">{c.category}</span>
                            <span className="text-xs text-[#8A867D]">{c.total} rating{c.total === 1 ? "" : "s"} · {c.nailed_pct}% nailed</span>
                          </div>
                          <div className="flex h-3.5 w-full overflow-hidden rounded-full bg-[#F0EFEA]">
                            {["nailed", "solid", "insufficient"].map((k) => c[k] > 0 ? (
                              <div key={k} title={`${RATING_META[k].label}: ${c[k]}`} style={{ width: `${(c[k] / c.total) * 100}%`, background: RATING_META[k].bar }} />
                            ) : null)}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-2 mt-5">
                      {Object.entries(RATING_META).map(([k, m]) => (
                        <span key={k} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#525252]">
                          <span className="w-3 h-3 rounded-full inline-block" style={{ background: m.bar }} /> {m.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border border-[#0D1B2A]/20 bg-white overflow-hidden" data-testid="ratings-notes-table">
                  <div className="px-4 py-3 border-b border-[#F0EFEA] bg-[#F5F3ED]">
                    <p className="label-caps">Where it's stumbling — verbatim</p>
                    <p className="text-xs text-[#8A867D] mt-1">Every "Insufficient" note, newest first. This IS the improvement backlog.</p>
                  </div>
                  {!ratings.insufficient_notes?.length ? (
                    <div className="p-10 text-center text-[#525252]" data-testid="ratings-notes-empty">
                      No "Insufficient" ratings yet — the Finder is holding up. Nice.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-[15px]">
                        <thead>
                          <tr className="text-left border-b border-[#F0EFEA] bg-[#FAF8F2]">
                            <th className="px-4 py-3 label-caps font-semibold">Piece</th>
                            <th className="px-4 py-3 label-caps font-semibold">Category</th>
                            <th className="px-4 py-3 label-caps font-semibold">Their note</th>
                            <th className="px-4 py-3 label-caps font-semibold">When</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ratings.insufficient_notes.map((n, i) => (
                            <tr key={n.find_id} data-testid={`ratings-note-row-${i}`} className={`border-b border-[#F7F6F1] align-top ${i % 2 ? "bg-[#FDFBF7]" : "bg-white"}`}>
                              <td className="px-4 py-3 font-medium text-[#0D1B2A] max-w-xs">
                                {n.title || <span className="text-[#B8B4AC]">Unnamed</span>}
                                {n.era ? <span className="block text-xs text-[#8A867D] font-normal mt-0.5">{n.era}{n.item_type ? ` · ${n.item_type}` : ""}</span> : null}
                              </td>
                              <td className="px-4 py-3 text-[#525252] whitespace-nowrap">{n.category || "—"}</td>
                              <td className="px-4 py-3 text-[#3A3730] max-w-md italic">{(n.note || "").trim() || <span className="text-[#B8B4AC] not-italic">(no note)</span>}</td>
                              <td className="px-4 py-3 text-[#8A867D] whitespace-nowrap">{fmtDate(n.rated_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ) : tab === "backtest" ? (
          <div data-testid="admin-backtest-tab">
            <p className="text-sm text-[#525252] leading-relaxed mb-6 max-w-2xl">
              <strong>Blind-scan calibration.</strong> Runs the Finder AI against each of your listings — cover photo only,
              zero metadata hint — then scores its guess against your verified Chairish data. Shows exactly where the AI
              is strong (green) and where it stumbles (red). This is your defense layer for the metadata flywheel.
            </p>

            <div className="border border-[#0D1B2A]/20 bg-white p-5 mb-6 flex flex-wrap items-center gap-3" data-testid="backtest-controls">
              {backtestJob?.status === "running" ? (
                <div className="flex items-center gap-3 flex-1">
                  <ArrowClockwise size={20} weight="bold" className="text-[#B47A3C] animate-spin" />
                  <div>
                    <p className="font-bold text-[#0D1B2A]">Running — {backtestJob.completed || 0} of {backtestJob.limit || 0} scored</p>
                    <p className="text-xs text-[#8A867D]">Started {fmtDate(backtestJob.started_at)}. Each scan takes 15-25s. Poll auto-refreshes.</p>
                  </div>
                </div>
              ) : (
                <>
                  <button onClick={() => runBacktest(25)} disabled={backtestBusy} data-testid="backtest-run-25"
                    className="h-10 px-4 bg-[#0D1B2A] text-[#FDFBF7] rounded-md text-sm font-bold hover:bg-black transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                    <Play size={13} weight="fill" /> Run 25 (~$0.50)
                  </button>
                  <button onClick={() => runBacktest(100)} disabled={backtestBusy} data-testid="backtest-run-100"
                    className="h-10 px-4 bg-[#B47A3C] text-white rounded-md text-sm font-bold hover:bg-[#9c6832] transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                    <Play size={13} weight="fill" /> Run 100 (~$2.00)
                  </button>
                  <button onClick={() => runBacktest(500)} disabled={backtestBusy} data-testid="backtest-run-all"
                    className="h-10 px-4 bg-white border border-[#0D1B2A] text-[#0D1B2A] rounded-md text-sm font-bold hover:bg-[#F5F3ED] transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                    <Play size={13} weight="fill" /> Run all (~$10)
                  </button>
                  {backtest?.has_results ? (
                    <button onClick={clearBacktest} disabled={backtestBusy} data-testid="backtest-clear"
                      className="h-10 px-3 text-xs text-[#B91C1C] hover:text-[#7F1D1D] font-semibold inline-flex items-center gap-1 ml-auto">
                      <Trash size={12} weight="fill" /> Clear all
                    </button>
                  ) : null}
                </>
              )}
            </div>

            {!backtest?.has_results ? (
              <div className="p-10 text-center text-[#525252] border border-[#0D1B2A]/20 bg-white" data-testid="backtest-empty">
                {backtestJob?.status === "running"
                  ? "First results will appear as scans complete…"
                  : "No backtest data yet. Click \"Run 25\" above to score the first 25 listings blind against your ground truth."}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="backtest-field-scores">
                  {[
                    ["overall", "Overall"],
                    ["title", "Title"],
                    ["era", "Era"],
                    ["maker", "Maker"],
                    ["style", "Style"],
                    ["materials", "Materials"],
                    ["category", "Category"],
                    ["dimensions", "Dimensions"],
                  ].map(([key, label]) => {
                    const v = backtest.field_means?.[key];
                    const pct = v == null ? "—" : `${Math.round(v * 100)}%`;
                    const cls = v == null
                      ? "bg-[#F5F3ED] text-[#8A867D] border-[#D6D3CD]"
                      : v >= 0.8
                      ? "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]"
                      : v >= 0.5
                      ? "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]"
                      : "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]";
                    return (
                      <div key={key} data-testid={`backtest-metric-${key}`} className={`border p-4 ${cls}`}>
                        <p className="font-serif-display text-3xl font-bold leading-none">{pct}</p>
                        <p className="label-caps mt-2">{label}</p>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-[#8A867D] italic mb-6">Scored on {backtest.total_scored} listing{backtest.total_scored === 1 ? "" : "s"}. Higher = closer to your Chairish ground truth. Reds = where the market priors offense will pay off.</p>

                {backtest.by_category?.length > 0 ? (
                  <div className="border border-[#0D1B2A]/20 bg-white p-5 mb-6" data-testid="backtest-by-category">
                    <p className="label-caps mb-1">Accuracy by category</p>
                    <p className="text-sm text-[#8A867D] mb-4">Where the Finder is sharpest vs. where it needs work.</p>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left border-b border-[#F0EFEA]">
                          <th className="py-2 label-caps font-semibold">Category</th>
                          <th className="py-2 label-caps font-semibold text-right">Pieces</th>
                          <th className="py-2 label-caps font-semibold text-right">Overall</th>
                          <th className="py-2 label-caps font-semibold text-right">Maker</th>
                          <th className="py-2 label-caps font-semibold text-right">Era</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backtest.by_category.map((c) => (
                          <tr key={c.category} data-testid={`backtest-cat-${c.category.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} className="border-b border-[#F7F6F1]">
                            <td className="py-2 font-semibold text-[#0D1B2A]">{c.category}</td>
                            <td className="py-2 text-right text-[#525252]">{c.total}</td>
                            <td className="py-2 text-right font-bold">{Math.round((c.mean_overall || 0) * 100)}%</td>
                            <td className="py-2 text-right text-[#525252]">{c.mean_maker == null ? "—" : `${Math.round(c.mean_maker * 100)}%`}</td>
                            <td className="py-2 text-right text-[#525252]">{c.mean_era == null ? "—" : `${Math.round(c.mean_era * 100)}%`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}

                <div className="border border-[#0D1B2A]/20 bg-white overflow-hidden" data-testid="backtest-worst-table">
                  <div className="px-4 py-3 border-b border-[#F0EFEA] bg-[#F5F3ED]">
                    <p className="label-caps">Worst 20 — where the AI stumbled hardest</p>
                    <p className="text-xs text-[#8A867D] mt-1">Your prompt-tuning + market-priors backlog, in your own inventory's language.</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[13px]">
                      <thead>
                        <tr className="text-left border-b border-[#F0EFEA] bg-[#FAF8F2]">
                          <th className="px-3 py-2 label-caps font-semibold">Score</th>
                          <th className="px-3 py-2 label-caps font-semibold">Piece</th>
                          <th className="px-3 py-2 label-caps font-semibold">True → AI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backtest.results.slice(0, 20).map((r, i) => (
                          <tr key={r.id} data-testid={`backtest-row-${i}`} className={`border-b border-[#F7F6F1] align-top ${i % 2 ? "bg-[#FDFBF7]" : "bg-white"}`}>
                            <td className="px-3 py-3 whitespace-nowrap">
                              <span className={`font-bold text-lg ${(r.scores?.overall || 0) >= 0.8 ? "text-[#166534]" : (r.scores?.overall || 0) >= 0.5 ? "text-[#92400E]" : "text-[#991B1B]"}`}>
                                {Math.round((r.scores?.overall || 0) * 100)}%
                              </span>
                            </td>
                            <td className="px-3 py-3 font-semibold text-[#0D1B2A] max-w-xs">
                              {r.true_data?.listing_headline || <span className="text-[#B8B4AC]">Unnamed</span>}
                              <span className="block text-xs text-[#8A867D] font-normal mt-0.5">
                                {r.true_data?.item_category || "—"} · {r.true_data?.period_era || "—"}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-xs text-[#525252] leading-relaxed max-w-md">
                              <div><span className="text-[#8A867D] font-semibold">Maker:</span> <em>{r.true_data?.maker || "—"}</em> → <strong>{r.ai_guess?.likely_designer || "(blank)"}</strong></div>
                              <div><span className="text-[#8A867D] font-semibold">Era:</span> <em>{r.true_data?.period_era || "—"}</em> → <strong>{r.ai_guess?.era || "(blank)"}</strong></div>
                              <div><span className="text-[#8A867D] font-semibold">Style:</span> <em>{r.true_data?.styles || "—"}</em> → <strong>{r.ai_guess?.style || "(blank)"}</strong></div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
        <>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8" data-testid="admin-metrics">
          {[
            { icon: Users, label: "Founding sellers", value: loading ? "…" : (data?.total ?? 0), testid: "admin-metric-total" },
            { icon: Crown, label: "Spots remaining", value: loading ? "…" : Math.max(0, (data?.spots_total ?? 100) - (data?.total ?? 0)), testid: "admin-metric-remaining" },
            { icon: ChatCircleText, label: "Left a note", value: loading ? "…" : withNotes, testid: "admin-metric-notes" },
          ].map((m) => (
            <div key={m.label} data-testid={m.testid} className="border border-[#0D1B2A]/20 bg-white p-6">
              <m.icon size={22} weight="fill" className="text-[#0D1B2A] mb-3" />
              <p className="font-serif-display text-3xl font-bold leading-none">{m.value}</p>
              <p className="label-caps mt-2">{m.label}</p>
            </div>
          ))}
        </div>

        <div className="flex justify-end mb-3">
          <button onClick={downloadCsv} disabled={downloading || !signups.length} data-testid="admin-export-csv" className="h-11 px-4 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium inline-flex items-center gap-2 hover:bg-black transition-transform active:scale-95 disabled:opacity-50">
            <FileCsv size={18} weight="fill" /> {downloading ? "Preparing…" : "Export CSV"}
          </button>
        </div>

        <div className="border border-[#0D1B2A]/20 bg-white overflow-hidden" data-testid="admin-signups-table">
          {loading ? (
            <div className="p-10 text-center text-[#8A867D]">Loading signups…</div>
          ) : signups.length === 0 ? (
            <div className="p-10 text-center text-[#525252]" data-testid="admin-empty">No founding signups yet. Share the <button onClick={() => navigate("/founding")} className="underline font-semibold hover:text-[#B47A3C]">/founding</button> page to start collecting.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[15px]">
                <thead>
                  <tr className="text-left border-b border-[#F0EFEA] bg-[#F5F3ED]">
                    <th className="px-4 py-3 label-caps font-semibold">#</th>
                    <th className="px-4 py-3 label-caps font-semibold">Name</th>
                    <th className="px-4 py-3 label-caps font-semibold">Email</th>
                    <th className="px-4 py-3 label-caps font-semibold">Their biggest headache</th>
                    <th className="px-4 py-3 label-caps font-semibold">Claimed</th>
                  </tr>
                </thead>
                <tbody>
                  {signups.map((s, i) => (
                    <tr key={s.email + i} data-testid={`admin-signup-row-${i}`} className={`border-b border-[#F7F6F1] align-top ${i % 2 ? "bg-[#FDFBF7]" : "bg-white"}`}>
                      <td className="px-4 py-3 font-semibold text-[#B47A3C]">{s.spot_number}</td>
                      <td className="px-4 py-3 font-medium text-[#0D1B2A]">{s.name || "—"}</td>
                      <td className="px-4 py-3 text-[#525252]"><a href={`mailto:${s.email}`} className="hover:text-[#B47A3C] hover:underline">{s.email}</a></td>
                      <td className="px-4 py-3 text-[#525252] max-w-md">{(s.note || "").trim() || <span className="text-[#B8B4AC]">—</span>}</td>
                      <td className="px-4 py-3 text-[#8A867D] whitespace-nowrap">{fmtDate(s.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}
      </main>
    </div>
  );
}
